import { Component } from '@theme/component';

/**
 * Store locator: text + radius + geolocation search over server-rendered store
 * cards, with a Google Map kept in sync with the result list and Places
 * autocomplete on the location field.
 *
 * The cards are rendered by Liquid and never re-created here. This only
 * reorders, hides and annotates them, so the list still works with JS disabled
 * and stays crawlable.
 */

const EARTH_RADIUS_MILES = 3958.8;
const DEFAULT_CENTER = { lat: 39.5, lng: -98.35 };
const MAPS_CALLBACK = '__mgStoreLocatorMapsReady';
const STOREFRONT_API_URL = '/api/2026-07/graphql.json';
const MAX_STORE_PAGES = 10;

const STORES_QUERY = `query Stores($after: String) {
  metaobjects(type: "store_locator", first: 250, after: $after) {
    pageInfo { hasNextPage endCursor }
    nodes {
      handle
      name: field(key: "name") { value }
      address1: field(key: "address_line_1") { value }
      address2: field(key: "address_line_2") { value }
      city: field(key: "city") { value }
      state: field(key: "state") { value }
      zip: field(key: "postal_code") { value }
      countryCode: field(key: "country_code") { value }
      phone: field(key: "phone") { value }
      lat: field(key: "latitude") { value }
      lng: field(key: "longitude") { value }
      directions: field(key: "google_maps_url") { value }
      type: field(key: "store_type") { value }
      status: field(key: "store_status") { value }
      markets: field(key: "excluded_markets") { value }
    }
  }
}`;

/** Shared across every instance so the Maps script is only ever requested once. */
let mapsLoader = null;

/**
 * @param {string} apiKey
 * @returns {Promise<any>} resolves with the google.maps namespace
 */
function loadGoogleMaps(apiKey) {
  if (mapsLoader) return mapsLoader;

  if (window.google && window.google.maps) {
    mapsLoader = Promise.resolve(window.google.maps);
    return mapsLoader;
  }

  mapsLoader = new Promise((resolve, reject) => {
    // Google requires a global callback; it is removed as soon as it fires.
    window[MAPS_CALLBACK] = () => {
      delete window[MAPS_CALLBACK];
      resolve(window.google.maps);
    };

    const script = document.createElement('script');
    script.src =
      'https://maps.googleapis.com/maps/api/js?libraries=places&loading=async&callback=' +
      MAPS_CALLBACK +
      '&key=' +
      encodeURIComponent(apiKey);
    script.async = true;
    script.addEventListener('error', () => reject(new Error('Google Maps failed to load')));
    document.head.appendChild(script);
  });

  return mapsLoader;
}

/**
 * Great-circle distance in miles.
 *
 * @param {{lat: number, lng: number}} from
 * @param {{lat: number, lng: number}} to
 * @returns {number}
 */
function haversineMiles(from, to) {
  const toRadians = (degrees) => (degrees * Math.PI) / 180;
  const deltaLat = toRadians(to.lat - from.lat);
  const deltaLng = toRadians(to.lng - from.lng);
  const a =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(toRadians(from.lat)) * Math.cos(toRadians(to.lat)) * Math.sin(deltaLng / 2) ** 2;

  return EARTH_RADIUS_MILES * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Google hands back bounds that cross the antimeridian for anything spanning
 * the Pacific, "United States" included, where west is numerically east of
 * east. The longitude test is written to survive that.
 *
 * @param {{north: number, south: number, east: number, west: number}} box
 * @param {{lat: number, lng: number}} point
 * @returns {boolean}
 */
function within(box, point) {
  if (point.lat < box.south || point.lat > box.north) return false;
  return box.west <= box.east
    ? point.lng >= box.west && point.lng <= box.east
    : point.lng >= box.west || point.lng <= box.east;
}

/**
 * Half the diagonal of a bounding box, in miles. Used to tell a country from a
 * neighbourhood without asking Google what kind of place it returned.
 *
 * @param {{north: number, south: number, east: number, west: number}} box
 * @returns {number}
 */
function boxRadiusMiles(box) {
  return haversineMiles(
    { lat: box.south, lng: box.west },
    { lat: box.north, lng: box.east }
  ) / 2;
}

class MgStoreLocator extends Component {
  /** @type {Array<{element: HTMLElement, index: number, search: string, name: string, coords: {lat: number, lng: number} | null, distanceElement: HTMLElement | null, distance: number | null, visible: boolean}>} */
  #stores = [];

  /** @type {HTMLInputElement | null} */
  #queryInput = null;

  /** @type {HTMLSelectElement | null} */
  #radiusSelect = null;

  /** @type {HTMLElement | null} */
  #list = null;

  /** @type {HTMLElement | null} */
  #emptyElement = null;

  /** @type {HTMLElement | null} */
  #statusElement = null;

  /** @type {HTMLElement | null} */
  #container = null;

  /** @type {{lat: number, lng: number} | null} */
  #origin = null;

  /** Set by Places autocomplete so a selected suggestion skips re-geocoding. */
  #pendingOrigin = null;

  /** @type {any} */
  #map = null;

  /** @type {any} */
  #geocoder = null;

  /** @type {any} */
  #infoWindow = null;

  /** @type {Map<number, any>} */
  #markers = new Map();

  /**
   * Where the radius overlay is drawn, which is only the searches a radius
   * applies to. A country or state search matches on the place itself, so a
   * circle around its centre would describe nothing.
   * @type {{lat: number, lng: number} | null}
   */
  #radiusOrigin = null;

  /** @type {any} */
  #radiusCircle = null;

  /** The default red pin dropped on the searched point, as the live locator does. @type {any} */
  #originMarker = null;

  /** @type {boolean} */
  #initialized = false;

  /** Translated copy, rendered into the section by Liquid. @type {Record<string, string>} */
  #strings = {};

  async connectedCallback() {
    super.connectedCallback();

    if (this.#initialized) return;
    this.#initialized = true;

    this.#queryInput = this.querySelector('[data-mg-query]');
    this.#radiusSelect = this.querySelector('[data-mg-radius]');
    this.#list = this.querySelector('[data-mg-list]');
    this.#emptyElement = this.querySelector('[data-mg-empty]');
    this.#statusElement = this.querySelector('[data-mg-status]');
    this.#container = this.querySelector('[data-mg-view-state]');
    this.#strings = this.#readStrings();

    this.#bindEvents();

    // Start Google Maps downloading now rather than after the store fetch.
    // loadGoogleMaps hands back a shared promise, so #initMap picks up this
    // same request instead of waiting a further second for its own.
    if (this.dataset.apiKey) loadGoogleMaps(this.dataset.apiKey).catch(() => {});

    await this.#loadStores();
    this.#collectStores();

    const home = this.#homeOrigin();
    if (home) this.#applyDistances(home, { show: false });
    this.#render(undefined, { sortByDistance: Boolean(home) });
    this.#initMap();
  }

  /**
   * Copy comes from the locale files through a JSON payload rather than being
   * hardcoded here, so the locator translates with the rest of the site.
   *
   * @returns {Record<string, string>}
   */
  #readStrings() {
    const node = this.querySelector('[data-mg-strings]');
    if (!node?.textContent) return {};
    try {
      return JSON.parse(node.textContent);
    } catch {
      return {};
    }
  }

  /**
   * @param {string} key
   * @param {Record<string, string | number>} [tokens]
   * @returns {string}
   */
  #t(key, tokens) {
    let value = this.#strings[key] ?? '';
    if (tokens) {
      for (const [token, replacement] of Object.entries(tokens)) {
        value = value.replaceAll(`[${token}]`, String(replacement));
      }
    }
    return value;
  }

  /**
   * Liquid cannot loop past 250 metaobjects and there are 744 stores, so the
   * full list comes from the Storefront API instead: three pages of 250, same
   * origin, using the public read-only token from theme settings.
   *
   * On success the server-rendered apothecaries are replaced wholesale, which
   * is also how an apothecary created after the first 250 entries still
   * reaches the storefront. On failure they are left exactly as they are.
   */
  async #loadStores() {
    const token = this.dataset.storefrontToken;
    if (!token || !this.#list) return;

    const nodes = [];
    let after = null;

    try {
      // Capped so a repeating cursor can never spin the loop.
      for (let page = 0; page < MAX_STORE_PAGES; page += 1) {
        const response = await fetch(STOREFRONT_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token,
          },
          body: JSON.stringify({ query: STORES_QUERY, variables: { after } }),
        });

        if (!response.ok) throw new Error(String(response.status));

        const payload = await response.json();
        if (payload.errors) throw new Error('storefront api rejected the query');

        const connection = payload.data?.metaobjects;
        if (!connection) throw new Error('no metaobjects in response');

        nodes.push(...connection.nodes);
        if (!connection.pageInfo.hasNextPage) break;
        after = connection.pageInfo.endCursor;
      }
    } catch {
      return;
    }

    const market = this.dataset.market ?? '';
    const hidden = (this.dataset.hiddenStatuses ?? '').split(',').filter(Boolean);

    const fragment = document.createDocumentFragment();
    for (const node of nodes) {
      const store = this.#toStore(node);
      if (hidden.includes(store.status)) continue;
      if (market && store.markets.includes(market)) continue;
      fragment.appendChild(this.#buildCard(store));
    }

    this.#list.replaceChildren(fragment);
  }

  /**
   * Clones the chevron the section rendered, so the icon has a single source
   * in snippets/mg-chevron.liquid rather than being duplicated here.
   *
   * @returns {SVGElement | null}
   */
  #chevron() {
    const template = this.querySelector('template[data-mg-chevron]');
    const svg = template?.content.firstElementChild;
    return svg ? /** @type {SVGElement} */ (svg.cloneNode(true)) : null;
  }

  /**
   * Flattens one Storefront API node into the shape the card builder wants.
   * Every field arrives as a string, including the coordinates.
   *
   * @param {Record<string, any>} node
   */
  #toStore(node) {
    const value = (key) => node[key]?.value ?? '';
    const lat = Number.parseFloat(value('lat'));
    const lng = Number.parseFloat(value('lng'));

    let markets = [];
    try {
      const parsed = JSON.parse(value('markets') || '[]');
      if (Array.isArray(parsed)) markets = parsed;
    } catch {
      // A malformed exclusion list means the store shows everywhere, which is
      // the same as an empty one.
    }

    return {
      handle: node.handle ?? '',
      name: value('name'),
      address1: value('address1'),
      address2: value('address2'),
      city: value('city'),
      state: value('state'),
      zip: value('zip'),
      countryCode: value('countryCode'),
      phone: value('phone'),
      directions: value('directions'),
      type: value('type') === 'shop' ? 'shop' : 'stockist',
      status: value('status').trim().toLowerCase(),
      markets,
      lat: Number.isFinite(lat) ? lat : null,
      lng: Number.isFinite(lng) ? lng : null,
    };
  }

  /**
   * Mirrors snippets/mg-store-card.liquid so a card built here is
   * indistinguishable from a server-rendered one.
   *
   * @param {Record<string, any>} store
   * @returns {HTMLLIElement}
   */
  #buildCard(store) {
    const isShop = store.type === 'shop';
    const hasCoords = store.lat !== null && store.lng !== null;
    const root = (this.dataset.storeRoot || '/').replace(/\/$/, '');
    const url = `${root}/pages/stores/${store.handle}`;

    const item = document.createElement('li');
    item.className = isShop ? 'mg-store mg-store--shop' : 'mg-store';
    item.setAttribute('data-mg-store', '');
    item.dataset.type = store.type;
    item.dataset.name = store.name;
    item.dataset.countryCode = store.countryCode;
    item.dataset.url = url;
    item.dataset.search = [
      store.name, store.address1, store.address2, store.city,
      store.state, store.zip, store.countryCode,
    ].filter(Boolean).join(' ').toLowerCase();
    if (hasCoords) {
      item.dataset.lat = String(store.lat);
      item.dataset.lng = String(store.lng);
    }

    const select = document.createElement('button');
    select.className = 'mg-store__select';
    select.type = 'button';
    select.setAttribute('data-mg-focus-marker', '');
    const selectLabel = document.createElement('span');
    selectLabel.className = 'visually-hidden';
    selectLabel.textContent = this.#t('showOnMap', { name: store.name });
    select.appendChild(selectLabel);

    const name = document.createElement('p');
    name.className = 'mg-store__name';
    if (isShop) {
      const nameLink = document.createElement('a');
      nameLink.href = url;
      nameLink.textContent = store.name;
      name.appendChild(nameLink);
    } else {
      name.textContent = store.name;
    }

    const distance = document.createElement('p');
    distance.className = 'mg-store__distance';
    distance.setAttribute('data-mg-distance', '');
    distance.hidden = true;

    const address = document.createElement('address');
    address.className = 'mg-store__address';
    const lines = [store.address1, store.address2];
    if (store.city) lines.push(store.state ? `${store.city}, ${store.state}` : store.city);
    if (store.countryCode) {
      lines.push(store.zip ? `${store.countryCode}, ${store.zip}` : store.countryCode);
    }
    lines.filter(Boolean).forEach((line, index, all) => {
      address.appendChild(document.createTextNode(line));
      if (index < all.length - 1) address.appendChild(document.createElement('br'));
    });

    item.append(select, name, distance, address);

    if (store.phone) {
      const phone = document.createElement('p');
      phone.className = 'mg-store__phone';
      const label = document.createElement('span');
      label.className = 'mg-store__phone-label';
      label.textContent = this.#t('phoneLabel');
      const link = document.createElement('a');
      link.href = `tel:${store.phone.replace(/[ ()]/g, '')}`;
      link.textContent = store.phone;
      phone.append(label, ' ', link);
      item.appendChild(phone);
    }

    const fallback = [store.address1, store.city, store.state, store.zip]
      .filter(Boolean)
      .join(' ');
    const destination = hasCoords ? `${store.lat},${store.lng}` : fallback;

    const links = document.createElement('p');
    links.className = 'mg-store__links';

    if (isShop) {
      const info = document.createElement('a');
      info.className = 'mg-store__link';
      info.href = url;
      info.append(this.#t('moreInfo'));
      const infoChevron = this.#chevron();
      if (infoChevron) info.append(infoChevron);
      links.appendChild(info);
    }

    const directions = document.createElement('a');
    directions.className = 'mg-store__link mg-store__link--directions';
    directions.href = store.directions
      || `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
    directions.target = '_blank';
    directions.rel = 'noopener';
    directions.append(this.#t('getDirections'));
    const chevron = this.#chevron();
    if (chevron) directions.append(chevron);
    links.appendChild(directions);
    item.appendChild(links);

    return item;
  }

  disconnectedCallback() {
    this.#markers.forEach((marker) => marker.setMap(null));
    this.#markers.clear();
  }

  #collectStores() {
    const cards = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-mg-store]')]);

    this.#stores = cards.map((element, position) => {
      // The index that matters is the card's position in the rendered list,
      // not whatever Liquid stamped on it.
      element.dataset.index = String(position);

      const lat = Number.parseFloat(element.dataset.lat ?? '');
      const lng = Number.parseFloat(element.dataset.lng ?? '');
      const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);

      return {
        element,
        index: position,
        search: (element.dataset.search ?? '').toLowerCase(),
        name: element.dataset.name ?? '',
        countryCode: (element.dataset.countryCode ?? '').toUpperCase(),
        type: element.dataset.type === 'shop' ? 'shop' : 'stockist',
        coords: hasCoords ? { lat, lng } : null,
        distanceElement: element.querySelector('[data-mg-distance]'),
        distance: null,
        visible: true,
      };
    });
  }

  #bindEvents() {
    this.querySelector('[data-mg-form]')?.addEventListener('submit', this.#onSubmit);
    this.querySelector('[data-mg-geolocate]')?.addEventListener('click', this.#onGeolocate);
    this.#radiusSelect?.addEventListener('change', this.#onRadiusChange);
    this.#queryInput?.addEventListener('input', this.#onQueryInput);

    this.addEventListener('click', this.#onClick);

    this.querySelectorAll('[data-mg-view]').forEach((button) => {
      button.addEventListener('click', this.#onViewToggle);
    });
  }

  /** @param {Event} event */
  #onSubmit = (event) => {
    event.preventDefault();
    this.#search();
  };

  #onRadiusChange = () => {
    // Only meaningful once we have somewhere to measure from.
    if (this.#origin) this.#search();
  };

  #onQueryInput = () => {
    // Typing invalidates any coordinates chosen from a previous suggestion.
    this.#pendingOrigin = null;

    if ((this.#queryInput?.value ?? '').trim() === '') {
      this.#origin = null;
      this.#radiusOrigin = null;
      this.#resetDistances();
      this.#render();
      this.#setStatus('');
    }
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('a')) return;

    const card = target.closest('[data-mg-store]');
    if (!(card instanceof HTMLElement)) return;

    const index = Number.parseInt(card.dataset.index ?? '', 10);
    if (!Number.isInteger(index)) return;

    this.#select(index, { pan: true });
    this.#setView('map');
  };

  /** @param {MouseEvent} event */
  #onViewToggle = (event) => {
    const button = /** @type {HTMLElement} */ (event.currentTarget);
    this.#setView(button.dataset.mgView === 'map' ? 'map' : 'list');
  };

  /** @param {'list' | 'map'} view */
  #setView(view) {
    if (!this.#container) return;
    this.#container.dataset.mgViewState = view;

    this.querySelectorAll('[data-mg-view]').forEach((button) => {
      const isActive = button instanceof HTMLElement && button.dataset.mgView === view;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });

    // The map sizes itself to a hidden container as 0x0, so nudge it back.
    if (view === 'map' && this.#map && window.google?.maps) {
      window.google.maps.event.trigger(this.#map, 'resize');
      this.#fitToVisible();
    }
  }

  /* ---------------------------------------------------------------- search */

  /**
   * Where the list opens before anyone searches. The live locator opens on New
   * York, so the theme does too, and the client can move it in section
   * settings without touching code.
   *
   * @returns {{lat: number, lng: number} | null}
   */
  #homeOrigin() {
    const lat = Number.parseFloat(this.dataset.homeLat ?? '');
    const lng = Number.parseFloat(this.dataset.homeLng ?? '');
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }

  /**
   * Every store in the place that was searched for, or null when the place is
   * small enough that the radius should decide.
   *
   * Countries match on country code, not on their bounding box: the box for
   * France reaches New Caledonia and the box for the United Kingdom reaches the
   * Falklands, so either one would sweep in most of the world.
   *
   * Anything smaller falls back to the box, which is honest for a state or a
   * county and stays out of the way for a street address.
   *
   * @param {Record<string, any>} located
   * @returns {Array<Record<string, any>> | null}
   */
  #inPlace(located) {
    if (located.isCountry && located.countryCode) {
      return this.#stores.filter((store) => store.countryCode === located.countryCode);
    }

    const box = located.bounds;
    if (!box || boxRadiusMiles(box) <= this.#radius()) return null;

    return this.#stores.filter((store) => store.coords && within(box, store.coords));
  }

  async #search() {
    const query = (this.#queryInput?.value ?? '').trim();
    const radius = this.#radius();

    if (query === '') {
      this.#origin = null;
      this.#radiusOrigin = null;
      this.#resetDistances();

      const home = this.#homeOrigin();
      if (home) this.#applyDistances(home, { show: false });
      this.#render(undefined, { sortByDistance: Boolean(home) });
      this.#setStatus('');
      return;
    }

    const needle = query.toLowerCase();
    const textMatches = this.#stores.filter((store) => store.search.includes(needle));

    // A suggestion picked from autocomplete already carries coordinates.
    const located = this.#pendingOrigin ?? (await this.#geocode(query));

    if (located) {
      this.#origin = located;
      this.#radiusOrigin = located;
      this.#applyDistances(located);

      // Searching a country or a state geocodes to one point in the middle of
      // it, and a radius around that point is meaningless: the centre of the
      // United States is a thousand miles from any store. Both cases widen the
      // search to the whole place, by different means.
      const inside = this.#inPlace(located);
      if (inside) {
        this.#radiusOrigin = null;
        this.#render(inside, { sortByDistance: true });
        this.#setStatus(
          inside.length > 0
            ? this.#t('areaResults', { count: inside.length, query })
            : this.#t('noneInArea', { query })
        );
        return;
      }

      const withinRadius = this.#stores.filter(
        (store) => store.distance !== null && store.distance <= radius
      );

      if (withinRadius.length > 0) {
        this.#render(withinRadius, { sortByDistance: true });
        this.#setStatus('');
        return;
      }

      if (textMatches.length > 0) {
        this.#render(textMatches, { sortByDistance: true });
        this.#setStatus(this.#t('radiusFallbackToText', { radius }));
        return;
      }

      this.#render([]);
      this.#setStatus(this.#t('noneInRadius', { radius, query }));
      return;
    }

    // No geocoder (no API key, or the lookup failed); text search still works.
    this.#origin = null;
    this.#radiusOrigin = null;
    this.#resetDistances();
    this.#render(textMatches);
    this.#setStatus(
      textMatches.length > 0 ? '' : this.#t('noMatch', { query })
    );
  }

  #onGeolocate = () => {
    if (!('geolocation' in navigator)) {
      this.#setStatus(this.#t('geolocationUnsupported'));
      return;
    }

    this.#setStatus(this.#t('locating'));

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const origin = { lat: position.coords.latitude, lng: position.coords.longitude };
        const radius = this.#radius();

        this.#origin = origin;
        this.#radiusOrigin = origin;
        this.#applyDistances(origin);

        const withinRadius = this.#stores.filter(
          (store) => store.distance !== null && store.distance <= radius
        );

        this.#render(withinRadius, { sortByDistance: true });
        this.#setStatus(
          withinRadius.length > 0 ? '' : this.#t('noResultsNearYou', { radius })
        );

        if (this.#map) this.#map.setCenter(origin);
      },
      (error) => {
        this.#setStatus(
          error.code === error.PERMISSION_DENIED
            ? this.#t('permissionDenied')
            : this.#t('locationFailed')
        );
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  };

  /**
   * @param {string} query
   * @returns {Promise<{lat: number, lng: number} | null>}
   */
  async #geocode(query) {
    if (!this.#geocoder) return null;

    // Google returns ZERO_RESULTS for a bare postcode passed as a free-text
    // address, so postcode-shaped queries are asked for as a postcode first,
    // scoped to the country the storefront is being browsed in.
    const requests = [{ address: query }];
    if (/^[0-9][0-9a-z][0-9a-z \-]{1,8}$/i.test(query)) {
      requests.unshift({
        componentRestrictions: { postalCode: query, country: this.dataset.country || 'US' },
      });
    }

    for (const request of requests) {
      try {
        const response = await this.#geocoder.geocode(request);
        const geometry = response?.results?.[0]?.geometry;
        const location = geometry?.location;
        if (!location) continue;

        // "bounds" is the real extent of the place and is only present for
        // areas, so a country carries one and a street address does not.
        const result = response.results[0];
        const box = geometry.bounds;
        const countryPart = (result.address_components ?? []).find(
          (part) => part.types.includes('country')
        );

        return {
          lat: location.lat(),
          lng: location.lng(),
          isCountry: (result.types ?? []).includes('country'),
          countryCode: (countryPart?.short_name ?? '').toUpperCase(),
          bounds: box
            ? {
                north: box.getNorthEast().lat(),
                east: box.getNorthEast().lng(),
                south: box.getSouthWest().lat(),
                west: box.getSouthWest().lng(),
              }
            : null,
        };
      } catch {
        // Fall through and try the next form of the query.
      }
    }

    return null;
  }

  /** @returns {number} */
  #radius() {
    const value = Number.parseFloat(this.#radiusSelect?.value ?? '');
    return Number.isFinite(value) ? value : Number.parseFloat(this.dataset.defaultRadius ?? '25');
  }

  /** @param {{lat: number, lng: number}} origin */
  #applyDistances(origin, { show = true } = {}) {
    for (const store of this.#stores) {
      store.distance = store.coords ? haversineMiles(origin, store.coords) : null;

      if (!store.distanceElement) continue;

      // Before a search the distances only decide the order. Printing "3,412
      // mi" next to every store would be noise, and the live locator does not
      // show one either.
      if (!show) {
        store.distanceElement.hidden = true;
        store.distanceElement.textContent = '';
        continue;
      }

      if (store.distance === null) {
        store.distanceElement.hidden = true;
        store.distanceElement.textContent = '';
      } else {
        store.distanceElement.hidden = false;
        store.distanceElement.textContent = this.#t('distance', { distance: store.distance.toFixed(1) });
      }
    }
  }

  #resetDistances() {
    for (const store of this.#stores) {
      store.distance = null;
      if (!store.distanceElement) continue;
      store.distanceElement.hidden = true;
      store.distanceElement.textContent = '';
    }
  }

  /* ---------------------------------------------------------------- render */

  /**
   * @param {Array<any>} [matches] stores to show; defaults to all
   * @param {{sortByDistance?: boolean}} [options]
   */
  #render(matches, options = {}) {
    const visible = matches ?? this.#stores;
    const visibleSet = new Set(visible);

    for (const store of this.#stores) {
      store.visible = visibleSet.has(store);
      store.element.hidden = !store.visible;
    }

    if (this.#list) {
      // Apothecaries always lead and stockists follow. Within each group the
      // nearest comes first, or the original Liquid order when there is no
      // distance to sort on, so a cleared search never leaves the list
      // scrambled.
      const byType = (a, b) => (a.type === b.type ? 0 : a.type === 'shop' ? -1 : 1);

      const ordered = options.sortByDistance
        ? [...visible].sort((a, b) => {
            const group = byType(a, b);
            if (group !== 0) return group;
            if (a.distance === null) return 1;
            if (b.distance === null) return -1;
            return a.distance - b.distance;
          })
        : [...this.#stores].sort((a, b) => byType(a, b) || a.index - b.index);

      const fragment = document.createDocumentFragment();
      for (const store of ordered) fragment.appendChild(store.element);
      this.#list.appendChild(fragment);
    }

    if (this.#emptyElement) this.#emptyElement.hidden = visible.length > 0;

    this.#syncMarkers();
  }

  /** @param {string} message */
  #setStatus(message) {
    if (this.#statusElement) this.#statusElement.textContent = message;
  }

  /* ------------------------------------------------------------------- map */

  async #initMap() {
    const apiKey = this.dataset.apiKey;
    const canvas = this.querySelector('[data-mg-map]');
    if (!apiKey || !(canvas instanceof HTMLElement)) return;

    let maps;
    try {
      maps = await loadGoogleMaps(apiKey);
    } catch {
      // A failed map must not take the list down with it.
      canvas.remove();
      this.classList.add('mg-locator--no-map');
      this.#setStatus(this.#t('mapFailed'));
      return;
    }

    this.#map = new maps.Map(canvas, {
      center: DEFAULT_CENTER,
      zoom: Number.parseInt(this.dataset.mapZoom ?? '12', 10),
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
      // Raster maps snap to whole zoom levels, which leaves the radius circle
      // sitting in the middle of a frame twice its size. Fractional zoom lets
      // fitBounds land on the circle exactly, the way the live locator looks.
      isFractionalZoomEnabled: true,
    });
    this.#geocoder = new maps.Geocoder();
    this.#infoWindow = new maps.InfoWindow({ maxWidth: 260 });

    for (const store of this.#stores) {
      if (!store.coords) continue;

      const marker = new maps.Marker({
        position: store.coords,
        map: this.#map,
        title: store.name,
        icon: this.#pinIcon(maps, store.type),
      });

      marker.addListener('click', () => this.#select(store.index, { pan: false }));
      this.#markers.set(store.index, marker);
    }

    this.#initAutocomplete(maps);
    this.#syncMarkers();
  }

  /**
   * Suggestion dropdown on the location field.
   *
   * @param {any} maps
   */
  #initAutocomplete(maps) {
    if (!this.#queryInput || !maps.places?.Autocomplete) return;

    try {
      const autocomplete = new maps.places.Autocomplete(this.#queryInput, {
        fields: ['geometry', 'formatted_address', 'name'],
        types: ['geocode'],
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        const location = place?.geometry?.location;
        if (!location) return;

        // A picked suggestion carries its own extent, so choosing "California"
        // from the dropdown widens the search the same way typing it does.
        const box = place.geometry.viewport;
        this.#pendingOrigin = {
          lat: location.lat(),
          lng: location.lng(),
          bounds: box
            ? {
                north: box.getNorthEast().lat(),
                east: box.getNorthEast().lng(),
                south: box.getSouthWest().lat(),
                west: box.getSouthWest().lng(),
              }
            : null,
        };
        this.#search();
      });
    } catch {
      // Places not enabled on the key; plain geocoding still handles search.
    }
  }

  /**
   * Apothecaries and stockists get different coloured pins, matching the live
   * locator. Colours come from the section settings so the client can change
   * them without touching code.
   * @param {typeof google.maps} maps
   * @param {string} type
   */
  #pinIcon(maps, type) {
    const isShop = type === 'shop';
    const fill = isShop
      ? this.dataset.pinShop || '#10069f'
      : this.dataset.pinStockist || '#95958e';
    const width = isShop ? 26 : 22;
    const height = isShop ? 36 : 30;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 36" width="${width}" height="${height}"><path fill="${fill}" d="M13 0C5.82 0 0 5.82 0 13c0 9.75 13 23 13 23s13-13.25 13-23c0-7.18-5.82-13-13-13z"/><circle cx="13" cy="13" r="4.6" fill="#fff"/></svg>`;

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new maps.Size(width, height),
      anchor: new maps.Point(width / 2, height),
    };
  }

  #syncMarkers() {
    if (!this.#map) return;

    for (const store of this.#stores) {
      const marker = this.#markers.get(store.index);
      if (!marker) continue;
      marker.setMap(store.visible ? this.#map : null);
    }

    this.#syncRadiusCircle();
    this.#fitToVisible();
  }

  /**
   * The pin on the searched point and the shaded circle covering the searched
   * radius, the way the live locator draws them. One marker and one circle are
   * kept and moved rather than recreated, so redrawing on every render does
   * not flicker.
   */
  #syncRadiusCircle() {
    if (!this.#map || !window.google?.maps) return;

    if (!this.#origin) {
      this.#originMarker?.setMap(null);
    } else {
      if (!this.#originMarker) {
        this.#originMarker = new window.google.maps.Marker({ zIndex: 999 });
      }
      this.#originMarker.setPosition(this.#origin);
      this.#originMarker.setMap(this.#map);
    }

    if (!this.#radiusOrigin) {
      this.#radiusCircle?.setMap(null);
      return;
    }

    const metres = this.#radius() * 1609.344;
    const stroke = this.dataset.pinShop || '#10069f';

    if (!this.#radiusCircle) {
      this.#radiusCircle = new window.google.maps.Circle({
        strokeColor: stroke,
        strokeOpacity: 0.35,
        strokeWeight: 1,
        fillColor: stroke,
        fillOpacity: 0.08,
        clickable: false,
      });
    }

    this.#radiusCircle.setOptions({ center: this.#radiusOrigin, radius: metres });
    this.#radiusCircle.setMap(this.#map);
  }

  #fitToVisible() {
    if (!this.#map || !window.google?.maps) return;

    // With a radius drawn, the circle is the search: frame all of it, so the
    // overlay never runs off the edge of the map.
    const circleBounds = this.#radiusOrigin ? this.#radiusCircle?.getBounds() : null;
    if (circleBounds) {
      this.#map.fitBounds(circleBounds, 0);
      return;
    }

    const positioned = this.#stores.filter((store) => store.visible && store.coords);
    if (positioned.length === 0) return;

    // Before any search, fitting every marker would zoom out to world scale as
    // soon as one store is on another continent. Open on the first store
    // instead, the way the live locator opens on its home city.
    if (!this.#origin) {
      this.#map.setCenter(this.#homeOrigin() ?? positioned[0].coords);
      this.#map.setZoom(Number.parseInt(this.dataset.mapZoom ?? '12', 10));
      return;
    }

    if (positioned.length === 1) {
      this.#map.setCenter(positioned[0].coords);
      this.#map.setZoom(Number.parseInt(this.dataset.mapZoom ?? '12', 10));
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    for (const store of positioned) bounds.extend(store.coords);
    if (this.#origin) bounds.extend(this.#origin);
    this.#map.fitBounds(bounds, 48);
  }

  /**
   * @param {number} index
   * @param {{pan: boolean}} options
   */
  #select(index, options) {
    const store = this.#stores.find((candidate) => candidate.index === index);
    if (!store) return;

    for (const candidate of this.#stores) {
      candidate.element.classList.toggle('is-selected', candidate.index === index);
    }

    if (!options.pan) {
      store.element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    const marker = this.#markers.get(index);
    if (!marker || !this.#map) return;

    if (options.pan && store.coords) this.#map.panTo(store.coords);

    if (this.#infoWindow) {
      this.#infoWindow.setContent(this.#buildInfoContent(store));
      this.#infoWindow.open({ anchor: marker, map: this.#map });
    }
  }

  /**
   * Info window body. Built as nodes rather than an HTML string so store names
   * from the admin can never be interpreted as markup.
   *
   * Recent Maps versions render their own header row holding the close button.
   * Feeding the name into that header keeps the close button aligned with the
   * title instead of floating above an empty band.
   *
   * @param {any} store
   * @returns {HTMLElement}
   */
  #buildInfoContent(store) {
    const content = document.createElement('div');
    content.className = 'mg-locator__info';

    const usesHeader = typeof this.#infoWindow.setHeaderContent === 'function';

    if (usesHeader) {
      this.#infoWindow.setHeaderContent(store.name);
    } else {
      const title = document.createElement('p');
      title.className = 'mg-locator__info-name';
      title.textContent = store.name;
      content.append(title);
    }

    const address = store.element.querySelector('.mg-store__address');
    if (address) {
      // Text nodes sit between the <br>s, so this recovers the address lines.
      const lines = [...address.childNodes]
        .filter((node) => node.nodeType === Node.TEXT_NODE)
        .map((node) => node.textContent.trim())
        .filter(Boolean);

      if (lines.length > 0) {
        const block = document.createElement('p');
        block.className = 'mg-locator__info-address';
        lines.forEach((line, position) => {
          if (position > 0) block.append(document.createElement('br'));
          block.append(document.createTextNode(line));
        });
        content.append(block);
      }
    }

    if (store.element.dataset.type === 'shop') {
      const link = document.createElement('a');
      link.className = 'mg-locator__info-link';
      link.href = store.element.dataset.url ?? '#';
      link.textContent = this.#t('moreInfo');
      const chevron = this.#chevron();
      if (chevron) link.append(chevron);
      content.append(link);
    }

    return content;
  }
}

if (!customElements.get('mg-store-locator')) {
  customElements.define('mg-store-locator', MgStoreLocator);
}
